import { cn, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';

import type { UserRole } from '../contractsMirror.js';
import { USER_ROLES, type UsersSearch } from '../searchParams.js';

/**
 * The role-filter pill row on the People & Access list. Clones the employees
 * `StatusFilterPills`: each pill is a `<Link>` that sets `?role` in the typed search
 * params, and shows its SERVER-computed count (22-06 `roleCounts`, filter-independent so
 * the counts stay stable while a filter is active). The "All" pill clears the role filter.
 * The active pill is the one place the orange accent is used here
 * (`bg-primary text-primary-foreground`); 44px targets; pills wrap (ES layout obligation,
 * I18N-05). Counts in IBM Plex Mono.
 */

const PILL_BASE =
  'inline-flex h-11 items-center gap-2 rounded-md border px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';
const PILL_ACTIVE = 'border-transparent bg-primary text-primary-foreground';
const PILL_INACTIVE = 'border-border bg-card text-foreground hover:bg-secondary';

/** Preserve the active text search when changing the role pill (deep-link parity). */
function pillSearch(search: UsersSearch, role?: UserRole): UsersSearch {
  const next: UsersSearch = {};
  if (search.q) next.q = search.q;
  if (role) next.role = role;
  return next;
}

export function RoleFilterPills({
  search,
  roleCounts,
  totalUsers,
}: {
  search: UsersSearch;
  roleCounts: Partial<Record<UserRole, number>>;
  totalUsers: number;
}) {
  const { t } = useTranslation();
  const activeRole = search.role;

  return (
    <section
      aria-label={t('users:filter.regionLabel')}
      className="mb-4 rounded-md border border-border bg-card p-6"
    >
      <h2 className="mb-3 font-semibold text-foreground text-sm">{t('users:filter.heading')}</h2>
      <div className="flex flex-wrap gap-2">
        <Link
          to="/users"
          search={pillSearch(search)}
          aria-current={activeRole ? undefined : 'true'}
          className={cn(PILL_BASE, activeRole ? PILL_INACTIVE : PILL_ACTIVE)}
        >
          {t('users:filter.all')}
          <span className="font-mono text-xs opacity-80">{totalUsers}</span>
        </Link>
        {USER_ROLES.map((role) => (
          <Link
            key={role}
            to="/users"
            search={pillSearch(search, role)}
            aria-current={activeRole === role ? 'true' : undefined}
            className={cn(PILL_BASE, activeRole === role ? PILL_ACTIVE : PILL_INACTIVE)}
          >
            {t(`users:roles.${role}`)}
            <span className="font-mono text-xs opacity-80">{roleCounts[role] ?? 0}</span>
          </Link>
        ))}
      </div>
    </section>
  );
}
